// An office can override a part's selectable options in the back office (CH-4366): hide the ones it
// never orders, and set which are selected by default. The override is the office's own copy of the
// part, kept on `office.parts` and keyed by part number, and each of its options matches the part's
// own by `value` — the override screen leaves an option's name and value uneditable, so the values
// here are always the part's own.
//
// It controls what the client sees, nothing more. The values it hides and defaults with are the
// part's own, which is what Workbase validates against when it rebuilds the order item from its own
// part documents.

const overridableTypes = ['MultiSelect', 'SingleSelect']

// Defaults are only meaningful for the plain selects the override screen offers. A `Showcase` or
// `StringOverride` part keeps its own `value` — a list it owns, not a selection.
const nonDefaultableSubTypes = ['Showcase', 'StringOverride']

// The office's checked options are its default value, a scalar for SingleSelect and a list for
// MultiSelect. One that no longer matches a visible option is dropped with it.
const officeDefault = (officePart, visibleValues) => {
  if (Array.isArray(officePart.value)) {
    const selected = officePart.value.filter((x) => visibleValues.includes(x))

    return selected.length ? selected : undefined
  }

  return visibleValues.includes(officePart.value) ? officePart.value : undefined
}

const isSelected = (value, optionValue) =>
  Array.isArray(value) ? value.includes(optionValue) : value !== undefined && value === optionValue

const overridePart = (part, officeParts, applyDefaults) => {
  const { detail } = part
  const officePart = officeParts.find((x) => x.number === detail?.number)

  if (!officePart || !overridableTypes.includes(detail?.type)) {
    return part
  }

  const options = (detail?.options || []).map((option) => {
    const hidden = officePart.options?.find((x) => x.value === option.value)?.hidden

    // Hiding governs what can be ordered next, not what was ordered already: on an order item
    // already placed, the option it was saved with stays. Flagged in place rather than dropped, so
    // prices stay aligned by option index.
    return hidden && (applyDefaults || !isSelected(detail?.value, option.value)) ? { ...option, hidden: true } : option
  })

  const overridden = { ...detail, options }

  // Adding a product takes the office's defaults, and an office entry with none at all cleared the
  // part's own. An order item already placed keeps the selection it was saved with.
  const overrideDefault = applyDefaults && !nonDefaultableSubTypes.includes(detail?.sub_type)

  if (overrideDefault) {
    const value = officeDefault(
      officePart,
      options.filter((option) => !option.hidden).map((option) => option.value)
    )

    if (value === undefined) {
      delete overridden.value
    } else {
      overridden.value = value
    }
  }

  return { ...part, detail: overridden }
}

// An office that hides every option leaves a select with nothing to pick: the part is neither shown
// nor required, or the product could never be ordered.
export const hasNoVisibleOptions = (detail) =>
  overridableTypes.includes(detail?.type) && !!detail?.options?.length && detail.options.every((option) => option?.hidden)

// `applyDefaults` is for a product being added — an order item already placed keeps the selections
// it was saved with.
const applyOfficePartOverrides = (products, officeParts, { applyDefaults = false } = {}) => {
  if (!products?.length || !officeParts?.length) {
    return products
  }

  return products.map((product) => ({
    ...product,
    parts: product.parts?.map((part) => overridePart(part, officeParts, applyDefaults)),
  }))
}

export default applyOfficePartOverrides
